import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, Chip, IconButton, CardContent, Grid, Tooltip as TooltipCustom, useMediaQuery, Backdrop, CircularProgress, useTheme, Stack } from '@mui/material';
import { Menu as MenuIcon, Help as HelpIcon } from '@mui/icons-material';
// app UI controller for toggling sidenav
import { useMaterialUIController, setMiniSidenav } from "context";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import SouthIcon from '@mui/icons-material/South';
import FilterPanelContainer from "components/Filter/Filterpopup"
import KpiMetricscards from "components/KPI/KpiMetricscards";
import TimeSeriescards from "components/TimeSeries/TimeSeriescards";
import HighlightsCards from "components/Highlights/Highlightscard";
import Visualizations from "components/Visualizations/Visualizations"
import PieLegendCard from "components/Visualizations/Piecharts"
import Barcharts from "components/Visualizations/Barcharts"
import LineChartSubBrand from "components/Visualizations/Linechartsubbrand";
import LineChartsale from "components/Visualizations/LineChartsale"
import InventoryCharts from "components/Visualizations/Inventorycharts";
import WaterfallChart from "components/Visualizations/waterfallchart";
import TopSkuBarChart from "components/Visualizations/SkuBarchart";
import Map from 'components/Visualizations/Map'
import SkuSingleBarChart from "components/Visualizations/SingleBarchartSku";
import datadrilldown from "assets/images/selectedicons/DataDrillDown.svg";
import drillDownArrow from "assets/images/selectedicons/DrillDownArrow.svg";
import datadrillhover from 'assets/images/selectedicons/Data Drill Down1.svg';
import FilterChip from "components/FilterChip/FilterChip";

const MarketingDashboard = ({ subcategoryToken: propSubcategoryToken }) => {
    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav } = controller;
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Below 900px
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Between 900px and 1200px

    // State for API data
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const header = document.querySelector('.MuiAppBar-root');
        if (header) {
            header.style.display = 'none';
        }
    }, []);

    // Toggle sidenav from the page menu icon
    const isTogglingSidenavRef = useRef(false);
    const handleToggleSidenav = useCallback(() => {
        if (isTogglingSidenavRef.current) return;
        isTogglingSidenavRef.current = true;
        // Defer to next frame to avoid blocking heavy charts paint
        requestAnimationFrame(() => {
            setMiniSidenav(dispatch, !miniSidenav);
            setTimeout(() => {
                isTogglingSidenavRef.current = false;
            }, 150);
        });
    }, [dispatch, miniSidenav]);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
            } catch (error) {
                console.error('Failed loading static mocks or transforming data:', error);
                setError('Failed to load static mock data. Check competitive_analysis/data/allMocks.json');
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, [location.search, propSubcategoryToken]);

    // KPI cards data
    const Kpiscardsdata = [
        {
            title: "Share of Voice (SOV)",
            values: [
                { label: "MTD", change: "0.6%", value: "12.7%", status: "increase" },
                { label: "QTD", change: "2.9%", value: "12.1%", status: "increase" },
                { label: "YTD", change: "8.2%", value: "11.4%", status: "increase" }
            ]
        },
        {
            title: "Brand Awareness",
            values: [
                { label: "MTD", change: "1.2%", value: "34.8%", status: "increase" },
                { label: "QTD", change: "3.4%", value: "33.9%", status: "increase" },
                { label: "YTD", change: "6.7%", value: "32.5%", status: "increase" }
            ]
        },
        {
            title: "Market Share % (Dollars Sold)",
            values: [
                { label: "MTD", change: "0.4%", value: "4.9%", status: "increase" },
                { label: "QTD", change: "1.7%", value: "4.7%", status: "increase" },
                { label: "YTD", change: "6.1%", value: "4.5%", status: "increase" }
            ]
        },
        {
            title: "Customer Acquisition Cost (CAC)",
            values: [
                { label: "MTD", change: "2.3%", value: "$41.72", status: "increase" },
                { label: "QTD", change: "4.7%", value: "$42.88", status: "increase" },
                { label: "YTD", change: "8.9%", value: "$44.96", status: "decrease" }
            ]
        },
        {
            title: "Customer Lifetime Value (CLV)",
            values: [
                { label: "MTD", change: "1.8%", value: "$312.84", status: "increase" },
                { label: "QTD", change: "4.6%", value: "$308.12", status: "increase" },
                { label: "YTD", change: "11.4%", value: "$296.41", status: "decrease" }
            ]
        },
        {
            title: "Net Promoter Score (NPS)",
            values: [
                { label: "MTD", change: "2.4%", value: "41.2", status: "increase" },
                { label: "QTD", change: "3.9%", value: "39.8", status: "increase" },
                { label: "YTD", change: "7.1%", value: "38.2", status: "increase" }
            ]
        }
    ];


    //second section cards
    const timeSeriesCardsdata = [
        {
            title: "Share of Voice (SOV)",
            values: [
                { label: "Nov 2024", change: "+3.7%", value: "12.2%", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: null, value: "12.7%", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+1.4%", value: "12.5%", status: "increase", arrow: "left" }
            ]
        },
        {
            title: "Brand Awareness",
            values: [
                { label: "Nov 2024", change: "+3.2%", value: "33.7%", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: null, value: "34.8%", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+1.1%", value: "34.4%", status: "increase", arrow: "left" }
            ]
        },
        {
            title: "Market Share (Dollar Sales)",
            values: [
                { label: "Nov 2024", change: "+2.2%", value: "4.8%", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: null, value: "4.9%", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+0.6%", value: "4.86%", status: "increase", arrow: "left" }
            ]
        },
        {
            title: "Customer Acquisition Cost (CAC)",
            values: [
                { label: "Nov 2024", change: "+3.7%", value: "$40.20", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: null, value: "$41.72", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+1.4%", value: "$41.14", status: "increase", arrow: "left" }
            ]
        },
        {
            title: "Customer Lifetime Value (CLV)",
            values: [
                { label: "Nov 2024", change: "+3.2%", value: "$303.10", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: null, value: "$312.84", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+1.1%", value: "$309.40", status: "increase", arrow: "left" }
            ]
        },
        {
            title: "Net Promoter Score (NPS)",
            values: [
                { label: "Nov 2024", change: "+2.2%", value: "40.3", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: null, value: "41.2", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+0.6%", value: "41.0", status: "increase", arrow: "left" }
            ]
        }
    ];




    const [openFilters, setOpenFilters] = useState(false);
    const handleToggleFilters = () => {
        setOpenFilters((prev) => !prev);
    };

    // Filter state
    const [activeFilter, setActiveFilter] = useState('All');
    const [filterLoading, setFilterLoading] = useState(false);

    // Define filter presets for each quick filter
    const getFilterPreset = useCallback((filterName) => {
        const allCompanies = ["Nutrabolt", "Bloom"];
        const allCategories = ["Functional Beverage", "Active Nutrition"];
        const allBrands = ["C4", "Cellucor", "XTEND", "Bloom"];
        const allSubBrands = ["C4", "C4 Performance", "C4 Smart Energy", "C4 Ultimate", "XTEND", "Bloom Sparkling Energy", "Bloom Pop"];
        const allRoutes = ["Ecommerce", "Direct", "DSD"];
        const allChannels = ["Ecommerce", "Convenience Chain", "Convenience Independent", "Drug", "Food", "Multi Outlet", "Mass"];

        switch (filterName) {
            case 'All':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Nutrabolt':
                return {
                    Company: ["Nutrabolt"],
                    Category: allCategories,
                    Brand: ["C4", "Cellucor", "XTEND"],
                    "Sub-Brand": ["C4", "C4 Performance", "C4 Smart Energy", "C4 Ultimate", "XTEND"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Bloom':
                return {
                    Company: ["Bloom"],
                    Category: ["Functional Beverage"],
                    Brand: ["Bloom"],
                    "Sub-Brand": ["Bloom Sparkling Energy", "Bloom Pop"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Functional Beverage':
                return {
                    Company: allCompanies,
                    Category: ["Functional Beverage"],
                    Brand: ["C4", "Bloom"],
                    "Sub-Brand": ["C4 Performance", "C4 Smart Energy", "C4 Ultimate", "Bloom Sparkling Energy", "Bloom Pop"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Active Nutrition':
                return {
                    Company: ["Nutrabolt"],
                    Category: ["Active Nutrition"],
                    Brand: ["C4", "Cellucor", "XTEND"],
                    "Sub-Brand": ["C4", "XTEND"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            default:
                return null;
        }
    }, []);

    const [filterPresets, setFilterPresets] = useState(() => getFilterPreset('All'));

    // Handle filter click with full-page loader
    const handleFilterClick = (filter) => {
        if (filter === activeFilter || filterLoading) return;

        setFilterLoading(true);
        setLoading(true); // Show full-page loader

        // Simulate loading delay
        setTimeout(() => {
            const preset = getFilterPreset(filter);
            setActiveFilter(filter);

            // Set the preset - this will trigger FilterPanelContainer to apply it
            if (preset) {
                setFilterPresets(preset);
            } else {
                setFilterPresets(null);
            }

            setFilterLoading(false);
            setLoading(false); // Hide full-page loader

            // Open filter panel for "Custom" filter
            if (filter === 'Custom') {
                setOpenFilters(true);
            }
        }, 800);
    };

    // Handle filter changes from FilterPanelContainer
    const handleFiltersChange = useCallback((newFilters) => {
        // Don't check for preset match if we're already in Custom mode
        // FilterPanelContainer's isApplyingPresetRef prevents callback when applying presets
        if (activeFilter === 'Custom') return;

        // Check if the current filters match any preset
        const presetNames = ['All', 'Nutrabolt', 'Bloom', 'Functional Beverage', 'Active Nutrition'];
        let matchedPreset = null;

        for (const presetName of presetNames) {
            const preset = getFilterPreset(presetName);
            if (preset) {
                const isMatching = Object.keys(preset).every(key => {
                    const presetValues = [...(preset[key] || [])].sort();
                    const currentValues = [...(newFilters[key] || [])].sort();
                    return presetValues.length === currentValues.length &&
                        presetValues.every((val, idx) => val === currentValues[idx]);
                });

                if (isMatching) {
                    matchedPreset = presetName;
                    break;
                }
            }
        }

        // If filters don't match any preset, switch to Custom
        if (!matchedPreset && activeFilter !== 'Custom') {
            setActiveFilter('Custom');
            setFilterPresets(null);
        }
    }, [activeFilter, getFilterPreset]);
    const [activeIdx, setActiveIdx] = React.useState(0);
    const kpiBriefData = [
        {
            title: "Month",
            value: "$ 65.2 M",
            lyValue: "59.12 M",
            pct: "5.8%",
            positive: true,
            status: "increase",
            glvalue: "$ 85.5 M"
        },
        {
            title: "Quarter",
            value: "$ 180.6 M",
            lyValue: "160.29 M",
            pct: "11.9%",
            positive: true,
            status: "increase",
            glvalue: "$ 207.3 M"
        },
        {
            title: "Year",
            value: "$ 859.1 M",
            lyValue: "823.03 M",
            pct: "5.6%",
            positive: true,
            status: "increase",
            glvalue: "$ 882.8 M"
        }
    ];

    const marketingSpend = [
        { label: "$75K–150K", value: 27, color: "#4A63E0" },
        { label: "$150K–300K", value: 9, color: "#2E3BA8" },
        { label: "$300K+", value: 6, color: "#1C275F" },
        { label: "$0–25K", value: 24, color: "#A3B8FF" },
        { label: "$25K–75K", value: 35, color: "#6F8DFF" },

    ];


    const websiteTraffic = [
        { label: "Referral", value: 31, color: " #F2A93B" },
        { label: "Paid Search", value: 20, color: "#64719A" },
        { label: "Organic Search", value: 13, color: "#3FAF6C" },
        { label: "Paid Social", value: 13, color: "#E46BAA" },
        { label: "Direct", value: 25, color: "#4A63E0" },

    ];
    const cacBreakdown = [

        { label: "Retail Media", value: 16, color: "#7ED321" },
        { label: "Influencers", value: 10, color: "#E1B649" },
        { label: "Amazon Ads", value: 10, color: "#BD10E0" },
        { label: "Email/SMS", value: 5, color: "#64719A" },
        { label: "Paid Social", value: 32, color: "#4A90E2" },
        { label: "Paid Search", value: 29, color: "#F2A93B" },

    ];
    const impressionsByRegion = [
        { label: "West", value: 18, color: "#3BA73B" },
        { label: "North", value: 7, color: "#B5CBEA" },
        { label: "South", value: 14, color: "#9B72C2" },
        { label: "South Central", value: 20, color: "#FFC081" },
        { label: "Northeast", value: 17, color: "#D93737" },
        { label: "Central", value: 30, color: "#DEDE96" },

    ];

    const impressionsByChannel = {
        title: "Impressions by Channel Over Time",

        xLabels: [
            "Dec. 2025", "Jan. 2025", "Feb. 2025", "Mar. 2025",
            "Apr. 2025", "May. 2025", "Jun. 2025", "Jul. 2025",
            "Aug. 2025", "Sep. 2025", "Oct. 2025", "Nov. 2025"
        ],

        series: [
            { label: "Paid Social", color: "#1A73E8" },
            { label: "Influencers (Paid)", color: "#F6C344" },
            { label: "Retail Media", color: "#34A853" },
            { label: "Amazon Ads", color: "#8B5CF6" },
            { label: "Programmatic/Display", color: "#D93025" }
        ],

        // values read from IMAGE (bar heights)
        dataMatrix: [
            // PaidSocial, Influencers, RetailMedia, AmazonAds, Programmatic
            [17, 8, 1, 3, 3],   // Dec → 32
            [15, 10, 1, 3, 6],  // Jan → 35
            [5, 17, 1, 3, 5],   // Feb → 31
            [13, 8, 1, 3, 6],   // Mar → 31
            [23, 4, 1, 3, 2],   // Apr → 33
            [24, 4, 1, 3, 4],   // May → 36
            [17, 6, 1, 3, 4],   // Jun → 31
            [12, 8, 1, 3, 10],  // Jul → 34
            [10, 9, 1, 3, 7],   // Aug → 30
            [8, 12, 2, 3, 15],  // Sep → 40
            [7, 14, 2, 3, 15],  // Oct → 41
            [6, 18, 2, 3, 13]   // Nov → 42
        ]
    };

    const SkuBarchartdata = {
        "title": "Impressions by Top 10 Campaigns",

        "yLabels": [
            "C4 Performance – Summer Energy Push",
            "C4 Smart Energy – Back-to-Campus",
            "C4 Ultimate – Gym Season Awareness",
            "XTEND – Hydration Always-On",
            "Bloom Sparkling – National Trial Drive",
            "C4 Performance – Walmart Feature",
            "C4 Smart Energy – Target Awareness",
            "C4 Performance – Amazon DSP On",
            "C4 Ultimate – Convenience Channel",
            "Bloom Pop – Social Sampling Burst"
        ],

        "series": [
            { "label": "Paid Social", "color": "#4285F4" },
            { "label": "Influencers (Paid)", "color": "#F5A623" },
            { "label": "Retail Media", "color": "#7ED321" },
            { "label": "Amazon Ads", "color": "#BD10E0" },
            { "label": "Programmatic/Display", "color": "#D0021B" }
        ],

        "dataMatrix": [
            [3, 16, 2, 4, 2],
            [3, 15, 1, 4, 3],
            [2, 13, 1, 4, 3],
            [2, 13, 1, 3, 2],
            [2, 12, 1, 3, 1],
            [2, 12, 1, 3, 1],
            [2, 12, 1, 2, 0],
            [2, 11, 1, 2, 0],
            [2, 10, 1, 2, 0],
            [1, 8, 1, 1, 0]
        ]
    }
    const [hover, setHover] = React.useState(false);

    const usaData = {
        CA: 140,
        NV: 20,
        AZ: 60,
        OR: 40,
        WA: 70,
        NM: 30,
        TX: 160,
        OK: 50,
        AR: 25,
        LA: 40,
        MS: 35,
        AL: 25,
        GA: 60,
        FL: 55,
        SC: 45,
        NC: 70,
        TN: 50,
        KY: 40,
        VA: 35,
        WV: 20,
        MD: 15,
        PA: 65,
        NY: 75,
        NJ: 30,
        CT: 18,
        MA: 25,
        NH: 12,
        ME: 10,
        MI: 45,
        OH: 40,
        IN: 35,
        IL: 80,
        IA: 15,
        MN: 12,
        WI: 22,
        MO: 60,
        KS: 18,
        NE: 10,
        SD: 8,
        ND: 5,
        CO: 35,
        UT: 22,
        ID: 15,
        MT: 8,
        WY: 6,
        AK: 5,
        HI: 5,
    };

    const thickBorderStates = [
        "CA", "AZ", "NM", "TX", "OK", "MO", "IL",
        "IN", "KY", "TN", "GA", "NC", "VA"
    ];

    const legendData = [
        { label: "$0–$5M", color: "#C8C6C4" },
        { label: "$5M–$10M", color: "#D0EBBE" },
        { label: "$10M–$20M", color: "#B7E29A" },
        { label: "$20M–$35M", color: "#99D56F" },
        { label: "$35M–$50M", color: "#6BB542" },
        { label: "$50M+", color: "#4C8C17" }
    ];
    return (

        <Box sx={{
            ml: 0,
            transition: "margin-left 0.25s ease",
            "& .MuiBox-root": { backgroundImage: "none" }
        }} style={{
            padding: isMobile ? '8px' : '0px 16px 0px 16px',
            fontFamily: 'inter !important'
        }}>
            {/* Loading Backdrop */}
            <Backdrop
                open={loading}
                sx={{
                    position: 'fixed',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                <CircularProgress color="primary" />
            </Backdrop>

            <Box
                sx={{
                    height: {
                        xs: "calc(100dvh - 140px)", // mobile header
                        md: "calc(100vh - 64px)",  // desktop header
                    },
                    overflowY: "auto",
                    // px: isMobile ? 1 : 2,
                    pb: "100px",
                    /* smooth scrolling */
                    scrollBehavior: "smooth",
                    WebkitOverflowScrolling: "touch", // iOS momentum

                    /* hide scrollbar */
                    scrollbarWidth: "none",          // Firefox
                    msOverflowStyle: "none",         // IE / Edge
                    "&::-webkit-scrollbar": {
                        display: "none",               // Chrome / Safari
                    },
                }}
            >
                {/* Section Title: KPI's */}
                <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
                    <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Marketing / </span><span style={{ fontWeight: 200, fontSize: "16px" }}>KPI's</span>
                </Typography>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <KpiMetricscards cardsData={Kpiscardsdata} kpiBriefData={kpiBriefData} />

                {/* Section Title: Time Series */}
                <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
                    <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Marketing / </span><span style={{ fontWeight: 200, fontSize: "16px" }}>Time Series</span>
                </Typography>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <TimeSeriescards timeSeriesCards={timeSeriesCardsdata} />




                <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '16px' }}>
                            <span style={{ color: "#1672B980", fontWeight: 200, fontSize: "16px" }}>Marketing / </span> <span style={{ fontSize: "16px", fontWeight: 200 }}>Visualizations</span>
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {["YTD", "QTD", "MTD"].map((label, idx) => (
                                <FilterChip
                                    key={label}
                                    label={label}
                                    idx={idx}
                                    activeIdx={activeIdx}
                                    onClick={setActiveIdx}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                </Box>
                <Grid
                    container
                    rowSpacing={2}
                    columnSpacing={2}
                    sx={{
                        mt: "26px !important",
                        alignItems: "stretch"   // keeps all cards same height
                    }}
                >
                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "0px !important", sm: "0px !important", md: "0px !important" }
                    }} >
                        <PieLegendCard title="Marketing Spend" data={marketingSpend} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Website Traffic by Source" data={websiteTraffic} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Customer Acquisition Cost Breakdown" data={cacBreakdown} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }} >
                        <PieLegendCard title="Impressions by Region" data={impressionsByRegion} />
                    </Grid>
                </Grid>
                <Grid container spacing={3} sx={{ mt: "10px !important" }}>
                    {/* LEFT CARD - USA MAP */}
                    <Grid item xs={12} md={4}>
                        <Map
                            title="Impressions by State Heatmap"
                            usaData={usaData}
                            thickBorderStates={thickBorderStates}
                            legendData={legendData}
                        />

                    </Grid>

                    {/* RIGHT CARD - BAR CHART */}
                    <Grid item xs={12} md={8}>
                        <SkuSingleBarChart
                            title={SkuBarchartdata.title}
                            yLabels={SkuBarchartdata.yLabels}
                            series={SkuBarchartdata.series}
                            dataMatrix={SkuBarchartdata.dataMatrix}
                            xTicks={[0, 5, 10, 15, 20, 25, 30]}
                            showUnit={true}
                        />
                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "34px !important" }}>
                    <Grid item xs={12}>
                        <Barcharts
                            title={impressionsByChannel.title}
                            xLabels={impressionsByChannel.xLabels}
                            series={impressionsByChannel.series}
                            dataMatrix={impressionsByChannel.dataMatrix}
                            yTicks={[0, 10, 20, 30, 40, 50]}
                            yAxisLabel="Count (M)"
                            valueFormatter={(v) => `${v}`}
                            rounded={false}
                        />
                    </Grid>
                </Grid>

            </Box>


        </Box>



    );
};

export default MarketingDashboard;